// Minimal compatibility layer for authStateManager
// This provides basic functionality while we transition to the new auth system

import { supabase } from './supabaseClient';
import { AuthChangeEvent, Session, User } from '@supabase/supabase-js';

export class AuthStateManager {
  private static instance: AuthStateManager;
  public listeners: Set<(event: AuthChangeEvent, session: Session | null) => void> = new Set();
  private isInitialized = false;
  private lastSessionUserId: string | null = null; // Track last session to prevent duplicate SIGNED_IN events

  static getInstance(): AuthStateManager {
    if (!AuthStateManager.instance) {
      AuthStateManager.instance = new AuthStateManager();
    }
    return AuthStateManager.instance;
  }

  initialize(): void {
    if (this.isInitialized) {
      console.log('⏭️ AuthStateManager: Already initialized, skipping. Listeners:', this.listeners.size);
      return;
    }
    
    console.log('🔐 AuthStateManager (compatibility): Initializing...');
    
    // Set up auth listener ONLY ONCE with STRICT filtering
    supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 AuthStateManager: Raw Supabase event:', event, 'Listeners:', this.listeners.size);
      
      // CRITICAL: Filter out problematic events at the SOURCE
      if (event === 'INITIAL_SESSION') {
        console.log('⏭️ AuthStateManager: Blocking INITIAL_SESSION to prevent tab reload cascades');
        // Still track the session user ID for duplicate detection
        this.lastSessionUserId = session?.user?.id || null;
        return; // Don't notify listeners of INITIAL_SESSION events
      }
      
      if (event === 'TOKEN_REFRESHED') {
        console.log('⏭️ AuthStateManager: Blocking TOKEN_REFRESHED to prevent unnecessary re-renders');
        return; // Don't notify listeners of TOKEN_REFRESHED events
      }
      
      // ADDITIONAL SAFEGUARD: Prevent duplicate SIGNED_IN events for the same user
      if (event === 'SIGNED_IN') {
        const currentUserId = session?.user?.id || null;
        if (this.lastSessionUserId === currentUserId && currentUserId !== null) {
          console.log('⏭️ AuthStateManager: Blocking duplicate SIGNED_IN event for same user:', currentUserId);
          return; // Don't notify listeners of duplicate SIGNED_IN events
        }
        // Update tracked user ID
        this.lastSessionUserId = currentUserId;
      } else if (event === 'SIGNED_OUT') {
        // Clear tracked user ID on sign out
        this.lastSessionUserId = null;
      }
      
      console.log('✅ AuthStateManager: Forwarding significant event to listeners:', event);
      
      // Only notify listeners of SIGNED_IN and SIGNED_OUT events
      this.listeners.forEach(listener => {
        try {
          listener(event, session);
        } catch (error) {
          console.error('Error in auth state listener:', error);
        }
      });
    });

    this.isInitialized = true;
    console.log('✅ AuthStateManager (compatibility): Initialized');
  }

  subscribe(callback: (event: AuthChangeEvent, session: Session | null) => void): () => void {
    this.listeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  async getSession(): Promise<{ session: Session | null; error: any }> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      return { session, error };
    } catch (error) {
      return { session: null, error };
    }
  }

  async getSessionInfo(): Promise<any> {
    const { session } = await this.getSession();
    return {
      hasSession: !!session,
      user: session?.user || null,
      expiresAt: session?.expires_at,
      accessToken: session?.access_token
    };
  }

  getListenerCount(): number {
    return this.listeners.size;
  }

  static async initializeAll(): Promise<void> {
    console.log('🚀 AuthStateManager (compatibility): initializeAll called');
    const instance = AuthStateManager.getInstance();
    instance.initialize();
  }

  static async cleanupAll(): Promise<void> {
    console.log('🚀 AuthStateManager (compatibility): cleanupAll called');
    const instance = AuthStateManager.getInstance();
    instance.listeners.clear();
  }
}

// Export singleton instance
export const authStateManager = AuthStateManager.getInstance();